import React, { useState, useEffect, useCallback, useRef } from 'react';
import { BackPill } from './BackPill';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Check, Pen, Feather, Trash2 } from 'lucide-react';
import { SignaturePad } from './SignaturePad';
import { WaxSeal } from './WaxSeal';
import { BACKEND_URL } from '../../utils/api';

/* ═══════════════════════════════════════════════════════════════════
   ROYAL DIARY — modelled 1:1 on QuoteBook.jsx
   • Page 0     : Sapphire-blue leather BOOK COVER
   • Page 1     : Compose-new (single-page entry — title + hand/type + Seal)
   • Pages 2..N : Previously sealed diary entries (read-only), newest first
   For mode='letter' the cover is skipped (letter is single page, no flips).
   ═══════════════════════════════════════════════════════════════════ */

const GOLD = '#D4AF37';
const GOLD_LIGHT = '#F5D97A';
const RUBY = '#A8201A';
const INK = '#1a1208';

/* ── Parchment chrome — matches QuoteBook PageFrame ── */
const PageFrame = ({ children }) => (
  <div
    className="absolute inset-0 flex flex-col"
    style={{
      background:
        'linear-gradient(180deg, #f8ebca 0%, #f0dfa8 50%, #d8c084 100%)',
      borderRadius: '4px 14px 14px 4px',
      boxShadow:
        'inset 0 0 70px rgba(168,107,31,0.18), inset 0 0 12px rgba(82,46,8,0.5), 0 8px 28px rgba(0,0,0,0.6)',
      overflow: 'hidden',
    }}
  >
    <div className="absolute inset-2 pointer-events-none"
      style={{ border: `2px solid ${GOLD}`, borderRadius: 6 }} />
    <div className="absolute inset-3.5 pointer-events-none"
      style={{ border: `1px solid rgba(168,107,31,0.5)`, borderRadius: 4 }} />
    {['1.5rem', 'calc(100% - 1.5rem)'].map((y) =>
      ['1.5rem', 'calc(100% - 1.5rem)'].map((x) => (
        <span
          key={`${x}-${y}`}
          aria-hidden
          style={{
            position: 'absolute', left: x, top: y,
            width: 14, height: 14,
            transform: 'translate(-50%, -50%) rotate(45deg)',
            border: `1px solid ${GOLD}`,
            background: 'rgba(212,175,55,0.18)',
            pointerEvents: 'none',
          }}
        />
      ))
    )}
    <div aria-hidden style={{
      position: 'absolute', top: 0, bottom: 0, left: 0, width: 16,
      background: 'linear-gradient(90deg, rgba(0,0,0,0.45), transparent)',
      pointerEvents: 'none',
    }} />
    {children}
  </div>
);

/* ── Sapphire-blue leather BOOK COVER (mirror of QuoteBook CoverPage) ── */
const DiaryCover = ({ empireName, profilePicture, isLetter }) => {
  const initial = (empireName || 'A')[0].toUpperCase();
  return (
    <div
      className="absolute inset-0 flex flex-col items-center justify-center text-center px-6 overflow-hidden"
      style={{
        background: isLetter
          ? 'radial-gradient(ellipse at 50% 38%, #6e1620 0%, #4a0a14 45%, #2a040a 100%)'
          : 'radial-gradient(ellipse at 50% 38%, #173a7a 0%, #0a1f4a 50%, #050b1f 100%)',
        borderRadius: '4px 14px 14px 4px',
        boxShadow:
          'inset 0 0 90px rgba(0,0,0,0.55), inset 0 0 18px rgba(0,0,0,0.7), 0 12px 32px rgba(0,0,0,0.7)',
      }}
    >
      <div aria-hidden className="absolute inset-0 pointer-events-none"
        style={{
          background:
            'radial-gradient(circle at 18% 22%, rgba(255,255,255,0.04) 0%, transparent 30%),' +
            'radial-gradient(circle at 78% 28%, rgba(0,0,0,0.18) 0%, transparent 28%),' +
            'radial-gradient(circle at 30% 78%, rgba(0,0,0,0.22) 0%, transparent 30%),' +
            'radial-gradient(circle at 82% 76%, rgba(255,225,180,0.05) 0%, transparent 32%)',
          mixBlendMode: 'overlay',
          opacity: 0.85,
        }}
      />
      <div aria-hidden className="absolute inset-0 pointer-events-none"
        style={{ background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.5) 100%)' }} />
      <div aria-hidden className="absolute inset-3 pointer-events-none"
        style={{
          border: `2.5px solid ${GOLD}`,
          borderRadius: 8,
          boxShadow: 'inset 0 0 0 1px rgba(255,241,184,0.4), 0 0 14px rgba(212,175,55,0.35)',
        }} />
      <div aria-hidden className="absolute inset-5 pointer-events-none"
        style={{ border: '1px solid rgba(212,175,55,0.55)', borderRadius: 5 }} />
      {[
        { top: 14, left: 14 }, { top: 14, right: 14 },
        { bottom: 14, left: 14 }, { bottom: 14, right: 14 },
      ].map((pos, i) => (
        <div key={i} aria-hidden className="absolute pointer-events-none"
          style={{
            ...pos, width: 22, height: 22, transform: 'rotate(45deg)',
            background: 'linear-gradient(135deg, #F5E6A0 0%, #D4AF37 45%, #8B6B1F 100%)',
            border: '1px solid rgba(60,40,8,0.6)',
            boxShadow: '0 0 8px rgba(212,175,55,0.55), inset 0 0 4px rgba(255,255,255,0.35)',
          }} />
      ))}
      <img
        src="/icons/empire_seal.png"
        alt=""
        aria-hidden
        draggable={false}
        className="absolute pointer-events-none select-none"
        style={{
          width: '70%', height: 'auto',
          left: '50%', top: '50%',
          transform: 'translate(-50%, -50%)',
          opacity: 0.07,
          filter: 'sepia(1) hue-rotate(-15deg) saturate(2.5) brightness(1.4)',
          mixBlendMode: 'screen',
        }}
      />
      <div aria-hidden style={{
        position: 'absolute', top: 0, bottom: 0, right: 0, width: 18,
        background: 'linear-gradient(270deg, rgba(0,0,0,0.6), transparent)',
        pointerEvents: 'none',
      }} />

      <p className="font-['Cormorant_Garamond'] uppercase tracking-[0.42em] text-[11px] mb-6 relative z-10"
        style={{ color: '#F5E6A0', textShadow: '0 1px 0 rgba(0,0,0,0.45)' }}>
        ~ {isLetter ? 'A Royal Letter' : 'The Empire Journal'} ~
      </p>

      <div className="relative mb-6 z-10" style={{ width: 110, height: 110 }}>
        <div aria-hidden className="absolute inset-0 rounded-full" style={{
          background: 'radial-gradient(circle, rgba(245,217,122,0.55) 0%, rgba(212,175,55,0) 65%)',
          filter: 'blur(8px)', transform: 'scale(1.35)',
        }} />
        <div className="absolute inset-0 rounded-full overflow-hidden" style={{
          border: '3px solid #D4AF37',
          boxShadow:
            'inset 0 0 0 2px rgba(255,241,184,0.7),' +
            '0 0 18px rgba(212,175,55,0.55),' +
            '0 6px 18px rgba(0,0,0,0.55)',
          background: profilePicture
            ? '#0a132a'
            : 'radial-gradient(circle at 50% 32%, #f6e6b6, #c69b3f 70%, #6b4612 100%)',
        }}>
          {profilePicture ? (
            <img src={profilePicture} alt={empireName} draggable={false}
              style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center" style={{
              fontFamily: '"Cormorant Garamond", Georgia, serif', fontWeight: 700,
              fontSize: 50, fontStyle: 'italic', color: '#3a2410',
              textShadow: '0 1px 0 rgba(255,241,184,0.6)',
            }}>{initial}</div>
          )}
        </div>
      </div>

      <h1 className="font-['Cormorant_Garamond'] font-bold leading-none relative z-10"
        style={{
          fontSize: 'clamp(40px, 12vw, 68px)', letterSpacing: '0.06em',
          background: 'linear-gradient(180deg, #FFF1B8 0%, #F5D97A 35%, #C69B3F 75%, #7A5418 100%)',
          WebkitBackgroundClip: 'text', backgroundClip: 'text', color: 'transparent',
          textShadow: '0 2px 0 rgba(0,0,0,0.4), 0 0 22px rgba(212,175,55,0.35)',
          filter: 'drop-shadow(0 1px 0 rgba(60,30,8,0.7))',
        }}>
        {empireName}
      </h1>

      <div className="my-5 w-32 h-[1.5px] relative z-10" style={{
        background: `linear-gradient(90deg, transparent, ${GOLD_LIGHT}, transparent)`,
        boxShadow: '0 0 8px rgba(245,217,122,0.5)',
      }} />

      <h2 className="font-['Cormorant_Garamond'] italic font-light relative z-10"
        style={{
          color: '#F5E6A0', fontSize: 'clamp(22px, 6vw, 30px)',
          letterSpacing: '0.08em', textShadow: '0 1px 0 rgba(0,0,0,0.5)',
        }}>
        {isLetter ? 'A Royal Letter' : 'The Empire Journal'}
      </h2>

      <div className="mt-10 opacity-80 relative z-10">
        <Feather size={32} style={{ color: GOLD_LIGHT, filter: 'drop-shadow(0 0 6px rgba(245,217,122,0.5))' }} />
      </div>

      <p className="absolute bottom-7 left-0 right-0 text-center font-['Cormorant_Garamond'] italic text-[11px] z-10"
        style={{ color: 'rgba(245,230,160,0.65)' }}>
        — turn the page to inscribe an entry —
      </p>
    </div>
  );
};

/* ── Compact pen-icon toggle (Gold pen / Ruby pen) ── */
const PenIconToggle = ({ value, onChange }) => {
  const isHand = value === 'hand';
  return (
    <div className="flex items-center gap-1 p-0.5 rounded-full flex-shrink-0"
      style={{ background: 'rgba(82,46,8,0.10)', border: '1px solid rgba(168,107,31,0.30)' }}
      data-testid="pen-toggle"
      role="group"
      aria-label="Pen mode">
      <button
        type="button"
        onClick={() => onChange('hand')}
        className="w-7 h-7 rounded-full flex items-center justify-center transition-all"
        style={{
          background: isHand ? `radial-gradient(circle, ${GOLD} 0%, #B7892D 100%)` : 'transparent',
          boxShadow: isHand ? `0 0 14px rgba(212,175,55,0.6)` : 'none',
        }}
        title="Gold pen — handwritten"
        aria-label="Gold pen — handwritten"
        aria-pressed={isHand}
        data-testid="pen-toggle-gold">
        <Pen size={13} color={isHand ? '#000' : GOLD} strokeWidth={2.4}
          style={{ filter: isHand ? 'none' : `drop-shadow(0 0 4px ${GOLD}aa)` }} />
      </button>
      <button
        type="button"
        onClick={() => onChange('type')}
        className="w-7 h-7 rounded-full flex items-center justify-center transition-all"
        style={{
          background: !isHand ? `radial-gradient(circle, ${RUBY} 0%, #5C0710 100%)` : 'transparent',
          boxShadow: !isHand ? `0 0 14px rgba(168,32,26,0.7)` : 'none',
        }}
        title="Ruby pen — typed"
        aria-label="Ruby pen — typed"
        aria-pressed={!isHand}
        data-testid="pen-toggle-ruby">
        <Pen size={13} color={!isHand ? '#fff' : RUBY} strokeWidth={2.4}
          style={{ filter: !isHand ? 'none' : `drop-shadow(0 0 4px ${RUBY}cc)` }} />
      </button>
    </div>
  );
};

/* ── Compose page (parchment) — single-page entry, mirrors QuoteBook ComposePage ── */
const ComposePage = ({
  empireName, isLetter,
  title, recipient, onTitleChange, onRecipientChange,
  penType, onPenChange,
  bodyImage, bodyText, onImageChange, onTextChange,
  onSave, saving, canSave,
}) => (
  <PageFrame>
    {/* Reduced vertical padding (Feb 2026 — user request: larger drawing
        canvas without shrinking the SEAL button).  Was pt-6 pb-5.
        Feb 17 2026 — user request: page must fit the exact window,
        never scroll.  Explicit overflow-hidden here + on the writing
        surface (line ~287) belt-and-suspenders the parchment against
        keyboard-driven viewport shifts on mobile. */}
    <div className="relative flex-1 flex flex-col px-4 sm:px-6 pt-3 pb-3 min-h-0 overflow-hidden"
      style={{ touchAction: 'manipulation' }}>

      {/* Header line — title input on left, pen toggle on right */}
      <div className="flex items-center gap-2">
        {isLetter ? (
          <input
            type="text"
            value={recipient}
            onChange={(e) => onRecipientChange(e.target.value.slice(0, 60))}
            placeholder="To… (optional)"
            className="flex-1 bg-transparent border-0 border-b border-amber-900/40 focus:border-amber-900 outline-none px-1 py-0.5 text-center font-['Cormorant_Garamond'] italic font-semibold text-base placeholder-amber-900/40"
            style={{ color: INK }}
            data-testid="letter-recipient-input"
          />
        ) : (
          <input
            type="text"
            value={title}
            onChange={(e) => onTitleChange(e.target.value.slice(0, 80))}
            placeholder="Entry title (optional)"
            className="flex-1 bg-transparent border-0 border-b border-amber-900/40 focus:border-amber-900 outline-none px-1 py-0.5 text-center font-['Cormorant_Garamond'] italic font-semibold text-base placeholder-amber-900/40"
            style={{ color: INK }}
            data-testid="note-title-input"
          />
        )}
        <PenIconToggle value={penType} onChange={onPenChange} />
      </div>

      <div className="mx-auto mt-1 mb-1 w-20 h-[1px]" style={{ background: GOLD }} />

      {/* Writing surface — fills the rest of the page; overflow-hidden
          so on-screen keyboard cannot push the whole page around */}
      <div className="flex-1 min-h-0 overflow-hidden">
        {penType === 'hand' ? (
          <div className="w-full h-full">
            <SignaturePad
              key={`hand-${penType}`}
              height={null}
              inkColor={INK}
              glow={false}
              onChange={onImageChange}
              placeholder={isLetter ? 'Write your letter here…' : 'Write your entry…'}
              testId="note-hand"
            />
          </div>
        ) : (
          <textarea
            value={bodyText}
            onChange={(e) => onTextChange(e.target.value.slice(0, 5000))}
            placeholder={isLetter ? 'Type your letter here…' : 'Type your entry…'}
            className="w-full h-full p-1 outline-none resize-none font-['Cormorant_Garamond'] italic text-center bg-transparent placeholder-amber-900/35"
            style={{ color: INK, fontSize: 'clamp(15px, 4.2vw, 18px)', lineHeight: 1.6 }}
            data-testid="note-type"
          />
        )}
      </div>

      {/* Signature line — compact so more space is left for the drawing */}
      <div className="text-right pr-2 mt-1 leading-tight">
        <p className="font-['Cormorant_Garamond'] italic text-[10px]" style={{ color: 'rgba(82,46,8,0.7)' }}>
          — to be sealed by
        </p>
        <p className="font-['Cormorant_Garamond'] font-bold tracking-wide"
          style={{ color: INK, fontSize: 'clamp(15px, 4.2vw, 19px)' }}>
          {empireName}
        </p>
      </div>

      {/* Char counter (only useful for typed) */}
      {penType === 'type' && (
        <div className="text-[10px] font-['Outfit'] text-amber-900/50 text-right mt-1">
          {bodyText.length}/5000
        </div>
      )}

      {/* Seal & Inscribe — gold foil button INSIDE the parchment */}
      <button
        type="button"
        onClick={onSave}
        disabled={saving || !canSave}
        data-testid="note-save-button"
        className="mt-3 w-full py-2.5 rounded-full font-['Outfit'] uppercase tracking-[0.18em] text-xs disabled:opacity-40 active:scale-95 transition-transform"
        style={{
          background: `linear-gradient(180deg, ${GOLD_LIGHT}, ${GOLD} 60%, #8B6B1F)`,
          color: '#1a0f02',
          boxShadow: '0 4px 14px rgba(168,107,31,0.55)',
        }}>
        {saving ? 'Sealing…' : (isLetter ? 'Seal Letter' : 'Seal & Inscribe')}
      </button>
    </div>
  </PageFrame>
);

/* ── Read-only sealed entry (mirrors QuoteBook QuotePage) ── */
const EntryPage = ({ entry, author, signatureUrl, onDelete, isOwner }) => {
  const firstPage = (entry.pages && entry.pages[0]) || null;
  const isHand = firstPage?.type === 'hand';
  const text = firstPage?.text || '';
  const img = firstPage?.image_data_url || entry.image_url || '';
  const date = entry.created_at;
  const title = entry.title || '';
  const pageCount = (entry.pages && entry.pages.length) || 1;

  return (
    <PageFrame>
      <div className="relative flex-1 flex flex-col px-5 sm:px-7 pt-7 pb-5 min-h-0">
        {isOwner && onDelete && (
          <button
            onClick={onDelete}
            className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full flex items-center justify-center"
            style={{ background: 'rgba(0,0,0,0.1)' }}
            data-testid="note-delete-btn">
            <Trash2 size={13} style={{ color: '#5a1a16' }} />
          </button>
        )}

        <p className="font-['Cormorant_Garamond'] uppercase tracking-[0.32em] text-[10px] text-center"
          style={{ color: 'rgba(82,46,8,0.7)' }}>
          ~ A Sealed Entry ~
        </p>
        <div className="mx-auto mt-2 mb-3 w-20 h-[1px]" style={{ background: GOLD }} />

        {title && (
          <h3 className="font-['Cormorant_Garamond'] font-semibold text-center mb-3"
            style={{ color: INK, fontSize: 'clamp(20px, 5.5vw, 28px)' }}>
            {title}
          </h3>
        )}

        {/* Body — image for hand, text for typed */}
        <div className="flex-1 min-h-0 flex items-center justify-center overflow-hidden">
          {isHand && img ? (
            <img
              src={img}
              alt={title || 'diary entry'}
              draggable={false}
              style={{
                maxWidth: '100%', maxHeight: '100%', width: 'auto', height: 'auto',
                objectFit: 'contain', filter: 'drop-shadow(0 2px 6px rgba(60,30,8,0.4))',
              }}
            />
          ) : (
            <p
              className="font-['Cormorant_Garamond'] italic leading-relaxed text-center px-2 overflow-y-auto max-h-full"
              style={{ color: '#2a0e0a', fontSize: 'clamp(15px, 4.2vw, 18px)' }}>
              {text || (entry.note || 'A sealed memory.')}
            </p>
          )}
        </div>

        <div className="mx-auto my-4 w-24 h-[1px]"
          style={{ background: `linear-gradient(90deg, transparent, ${GOLD}, transparent)` }} />

        {/* Centered author block + wax seal */}
        <div className="text-center">
          <p className="font-['Cormorant_Garamond'] italic text-[11px]"
            style={{ color: 'rgba(82,46,8,0.7)' }}>
            — sealed by
          </p>
          <p className="font-['Cormorant_Garamond'] font-bold tracking-wide"
            style={{ color: INK, fontSize: 'clamp(18px, 4.8vw, 22px)' }}>
            {author}
          </p>
          {date && (
            <p className="font-['Outfit'] text-[10px] mt-0.5"
              style={{ color: 'rgba(58,20,16,0.55)' }}>
              {new Date(date).toLocaleDateString('en-GB', {
                day: '2-digit', month: 'short', year: 'numeric',
              })}
              {pageCount > 1 ? ` · ${pageCount} pages` : ''}
            </p>
          )}
          <div className="mt-2 flex justify-center">
            <WaxSeal signatureUrl={signatureUrl} size={48} />
          </div>
        </div>
      </div>
    </PageFrame>
  );
};

export const NotebookEditor = ({ mode = 'note', onClose, onSaved, user }) => {
  const isLetter = mode === 'letter';
  const empireName = user?.nickname || user?.email?.split('@')[0] || 'Sovereign';

  /* Compose state — a single page, hand or typed. */
  const [penType, setPenType] = useState('hand');     // 'hand' | 'type'
  const [bodyImage, setBodyImage] = useState(null);
  const [bodyText, setBodyText] = useState('');
  const [title, setTitle] = useState('');
  const [recipient, setRecipient] = useState('');

  /* Feed of previously sealed entries (notes only — letters skip cover/feed). */
  const [entries, setEntries] = useState([]);

  /* Page state — 0 = cover, 1 = compose, 2..N = sealed entries. */
  const [page, setPage] = useState(0);
  const [direction, setDirection] = useState(1);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [savedFlash, setSavedFlash] = useState(false);

  const totalPages = isLetter ? 1 : 2 + entries.length;

  const switchPenMode = (t) => {
    if (penType === t) return;
    setPenType(t);
    setBodyImage(null);
    setBodyText('');
  };

  const load = useCallback(async () => {
    if (isLetter) return;
    try {
      const { data } = await axios.get(`${BACKEND_URL}/api/notebooks/feed/note?limit=200`);
      setEntries(Array.isArray(data) ? data : []);
    } catch (e) {
      setEntries([]);
    }
  }, [isLetter]);

  useEffect(() => { load(); }, [load]);

  /* Letters skip the cover (single page, fast). */
  useEffect(() => { if (isLetter) setPage(1); }, [isLetter]);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  const goPrev = () => {
    if (isLetter) return;
    setDirection(-1);
    setPage((p) => Math.max(0, p - 1));
  };
  const goNext = () => {
    if (isLetter) return;
    setDirection(1);
    setPage((p) => Math.min(totalPages - 1, p + 1));
  };

  /* Touch swipe — REAL BOOK gesture:
       swipe LEFT→RIGHT (dx>0) → PREVIOUS page (back to cover)
       swipe RIGHT→LEFT (dx<0) → NEXT page */
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const onTouchStart = (e) => {
    if (isLetter) return;
    touchStartX.current = e.touches[0].clientX;
    touchStartY.current = e.touches[0].clientY;
  };
  const onTouchEnd = (e) => {
    if (isLetter) return;
    const sx = touchStartX.current; const sy = touchStartY.current;
    touchStartX.current = null; touchStartY.current = null;
    if (sx == null) return;
    const dx = e.changedTouches[0].clientX - sx;
    const dy = e.changedTouches[0].clientY - (sy ?? 0);
    if (Math.abs(dx) < 60) return;
    if (Math.abs(dy) > Math.abs(dx)) return;
    if (dx < 0) goNext(); else goPrev();
  };

  const canSave = penType === 'hand' ? !!bodyImage : !!bodyText.trim();

  const handleSave = async () => {
    if (!canSave) {
      setError('Please add some content first.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const titleStr = isLetter
        ? (recipient ? `To: ${recipient}` : 'Letter')
        : (title || '');
      const payloadPage = penType === 'hand'
        ? { type: 'hand', image_data_url: bodyImage }
        : { type: 'type', text: bodyText.trim() };
      await axios.post(`${BACKEND_URL}/api/notebooks`, {
        category: isLetter ? 'letter' : 'note',
        title: titleStr,
        pages: [payloadPage],
      });
      // reset compose
      setBodyImage(null);
      setBodyText('');
      setTitle('');
      setSavedFlash(true);
      // Refresh feed and flip to the newly-sealed page after the ceremony.
      if (!isLetter) {
        await load();
        setTimeout(() => {
          setSavedFlash(false);
          setDirection(1);
          setPage(2);   // newest entry sits at index 2 (0=cover, 1=compose)
        }, 1300);
      } else {
        setTimeout(() => {
          onSaved && onSaved();
          onClose && onClose();
        }, 1100);
      }
      onSaved && onSaved();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not seal this. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const removeEntry = async (id) => {
    if (!window.confirm('Remove this entry from your Empire Journal?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/memories/${id}`);
      await load();
      setPage((p) => (p > 1 ? Math.max(1, p - 1) : p));
    } catch (e) { /* ignore */ }
  };

  /* Build the current page node */
  let pageNode;
  if (page === 0 && !isLetter) {
    pageNode = <DiaryCover empireName={empireName} profilePicture={user?.profile_picture} isLetter={false} />;
  } else if (page === 1 || isLetter) {
    pageNode = (
      <ComposePage
        empireName={empireName}
        isLetter={isLetter}
        title={title}
        recipient={recipient}
        onTitleChange={setTitle}
        onRecipientChange={setRecipient}
        penType={penType}
        onPenChange={switchPenMode}
        bodyImage={bodyImage}
        bodyText={bodyText}
        onImageChange={setBodyImage}
        onTextChange={setBodyText}
        onSave={handleSave}
        saving={saving}
        canSave={canSave}
      />
    );
  } else {
    const entry = entries[page - 2];
    if (entry) {
      pageNode = (
        <EntryPage
          entry={entry}
          author={empireName}
          signatureUrl={user?.signature_data_url}
          onDelete={() => removeEntry(entry.id)}
          isOwner={true}
        />
      );
    }
  }

  /* Helper hint */
  const hint = (() => {
    if (isLetter) return 'inscribe your letter, then seal it';
    if (page === 0) return 'tap ▶ to inscribe an entry';
    if (page === 1) return entries.length > 0
      ? 'tap ▶ to read previous entries'
      : 'inscribe your first entry, then seal it';
    return page === totalPages - 1
      ? 'this is your earliest entry'
      : 'tap ◀ ▶ to turn the page';
  })();

  return (
    <div
      className="fixed inset-0 z-[140] flex flex-col imperium-shell"
      style={{
        background:
          'radial-gradient(ellipse at top, #0a1530 0%, #04081a 60%, #000 100%)',
      }}
      onClick={onClose}
      data-testid={`${mode}-editor-modal`}
    >
      {/* Top bar — mirrors QuoteBook */}
      <div
        onClick={(e) => e.stopPropagation()}
        className="flex items-center justify-between px-4 py-3 flex-shrink-0"
        style={{
          paddingTop: 'calc(var(--safe-area-inset-top, env(safe-area-inset-top, 0px)) + 12px)',
          borderBottom: '1px solid rgba(212,175,55,0.18)',
        }}
      >
        <BackPill onClick={onClose} accent="#C8A36A" testId={`close-${mode}-editor`}/>
        <div className="flex flex-col items-center">
          <h2
            className="font-['Cormorant_Garamond'] text-xl font-light tracking-wide flex items-center gap-2"
            style={{ color: GOLD_LIGHT }}>
            <Feather size={16} />
            {isLetter ? 'Royal Letter' : 'Empire Journal'}
          </h2>
          <p className="font-['Outfit'] text-[10px] tracking-[0.18em] text-white/30 mt-0.5">
            {isLetter
              ? 'NEW LETTER'
              : page === 0
                ? 'COVER'
                : page === 1
                  ? 'NEW ENTRY'
                  : `PAGE ${page - 1} / ${entries.length}`}
          </p>
        </div>
        <div className="w-6" />
      </div>

      {/* Book stage — expanded to fill space freed by removed bottom bar.
          Swipe RIGHT → next, swipe LEFT → prev (→ cover). */}
      <div
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
        className="flex-1 flex items-center justify-center px-2 pb-3 min-h-0 overflow-hidden"
        style={{ paddingBottom: 'calc(12px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))' }}>
        <div
          className="relative w-full max-w-[480px] h-full"
          style={{ perspective: '1800px' }}>
          <AnimatePresence custom={direction} mode="wait">
            <motion.div
              key={page}
              custom={direction}
              initial={{ rotateY: direction > 0 ? 170 : -170, opacity: 0 }}
              animate={{ rotateY: 0, opacity: 1 }}
              exit={{ rotateY: direction > 0 ? -170 : 170, opacity: 0 }}
              transition={{
                rotateY: { duration: 0.7, ease: [0.55, 0.05, 0.25, 0.98] },
                opacity: { duration: 0.55 },
              }}
              style={{
                position: 'absolute', inset: 0,
                transformOrigin: direction > 0 ? 'left center' : 'right center',
                transformStyle: 'preserve-3d',
                backfaceVisibility: 'hidden',
                boxShadow: '0 8px 28px rgba(0,0,0,0.6), inset 0 0 40px rgba(0,0,0,0.25)',
              }}>
              {pageNode}
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      {isLetter && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="flex-shrink-0 px-6 pt-1 text-center"
          style={{ paddingBottom: 'calc(56px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))', boxSizing: 'border-box' }}>
          <p className="font-['Cormorant_Garamond'] italic text-xs text-white/40">
            {hint}
          </p>
        </div>
      )}

      {error && (
        <div className="absolute bottom-20 left-0 right-0 text-center font-['Outfit'] text-[11px] text-red-400 pointer-events-none">
          {error}
        </div>
      )}

      {/* Sealed ceremony overlay — mirrors QuoteBook */}
      <AnimatePresence>
        {savedFlash && (
          <motion.div
            key="sealed-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22 }}
            className="absolute inset-0 z-30 flex flex-col items-center justify-center"
            style={{
              background:
                'radial-gradient(ellipse at center, rgba(168,107,31,0.32) 0%, rgba(0,0,0,0.92) 60%, #000 100%)',
              backdropFilter: 'blur(4px)',
            }}>
            <motion.div
              initial={{ scale: 0.4, rotate: -8, opacity: 0 }}
              animate={{ scale: 1, rotate: 0, opacity: 1 }}
              transition={{ type: 'spring', stiffness: 220, damping: 14 }}>
              <WaxSeal signatureUrl={user?.signature_data_url} size={120} />
            </motion.div>
            <motion.p
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.25, duration: 0.4 }}
              className="mt-5 font-['Cormorant_Garamond'] italic text-2xl"
              style={{ color: GOLD_LIGHT }}>
              Sealed.
            </motion.p>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.6 }}
              transition={{ delay: 0.45, duration: 0.4 }}
              className="font-['Outfit'] uppercase tracking-[0.3em] text-[10px] mt-1"
              style={{ color: GOLD }}>
              {isLetter ? 'Sent to your Bliss Gallery' : 'Inscribed in your Empire Journal'}
            </motion.p>
            {/* Fallback gold check for letters (no auto-flip) */}
            {isLetter && (
              <div className="mt-4 w-14 h-14 rounded-full flex items-center justify-center"
                style={{ background: GOLD, boxShadow: '0 0 24px rgba(212,175,55,0.5)' }}>
                <Check size={28} color="#000" strokeWidth={3} />
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotebookEditor;
